import { range } from 'es-toolkit';

/**
 * Generates an array of ad duration options within a specified range.
 *
 * @param start - The starting range duration in seconds
 * @param end - The ending range duration in seconds
 * @param step - The increment between each option in seconds
 * @returns An array of objects with label and value properties
 *
 * @example
 * // Returns options like:
 * // [
 * //   { label: "00:30 (30s)", value: 30 },
 * //   { label: "01:00 (60s)", value: 60 },
 * //   { label: "01:30 (90s)", value: 90 }
 * // ]
 * generateAdDurationOptions(30, 90, 30);
 */
export const generateAdDurationOptions = (start: number, end: number, step: number) => {
  return range(start, end + step, step).map((value) => {
    return { label: convertNumberToDurationLabel(value), value };
  });
};

/**
 * Generates an array of ad duration options from a provided list of durations.
 *
 * @param durations - The duration to create the objects for.
 * @returns An array of objects with label and value properties
 *
 * @example
 * // Returns options like:
 * // [
 * //   { label: "00:30 (30s)", value: 30 },
 * //   { label: "01:00 (60s)", value: 60 },
 * //   { label: "01:30 (90s)", value: 90 }
 * // ]
 * createAdDurationsFromArray([30, 90, 30]);
 */
export const createAdDurationsFromArray = (durations: number[]) => {
  return durations.map((value) => {
    return { label: convertNumberToDurationLabel(value), value };
  });
};

/**
 * Converts a number duration into the duration label used in the ad duration control.
 */
export const convertNumberToDurationLabel = (duration: number) => {
  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;
  const formattedTime = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  return `${formattedTime} (${duration}s)`;
};
